import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Audio Assets Map
export const SFX_ASSETS = {
    // UI Interactions
    click: '/assets/soundfx/wood-1.wav',
    hover: '/assets/soundfx/keypress-009.wav',
    back: '/assets/soundfx/generated-001_slowest.wav',
    type: '/assets/soundfx/keypress-001.wav',

    // Success / Progress
    success: '/assets/soundfx/Accept.mp3',
    fail: '/assets/soundfx/error_failed.mp3',
    levelUp: '/assets/soundfx/Rise05.aif',
    unlock: '/assets/soundfx/Rise01.aif',
    complete: '/assets/soundfx/Rise06.aif',
    glory: '/assets/soundfx/Rise07.aif',

    // Negative / Alerts
    error: '/assets/soundfx/nope.mp3',
    alarm: '/assets/soundfx/Alarm.aif',
};

export type SoundKey = keyof typeof SFX_ASSETS;

interface SfxStore {
    isMuted: boolean;
    volume: number;
    activeSounds: HTMLAudioElement[];

    // Actions
    play: (key: SoundKey) => void;
    stopAll: () => void;
    toggleMute: () => void;
    setVolume: (val: number) => void;
}

export const useSfx = create<SfxStore>()(
    persist(
        (set, get) => ({
            isMuted: false,
            volume: 0.2, // Default Lowered to 20%
            activeSounds: [],

            play: (key) => {
                const state = get();
                if (state.isMuted) return;

                const src = SFX_ASSETS[key];
                if (!src) return;

                // Stop overlapping sounds for UI events to prevent "machine gun" effect
                if (key === 'hover' || key === 'type' || key === 'click') {
                    // Filter activeSounds to find if this specific src is already playing
                    const duplicates = state.activeSounds.filter(a => a.src.endsWith(src) || a.src === src);

                    if (duplicates.length > 0) {
                        duplicates.forEach(a => {
                            a.pause();
                            a.currentTime = 0;
                        });
                        // Remove from state
                        set(s => ({ activeSounds: s.activeSounds.filter(a => !duplicates.includes(a)) }));
                    }
                }

                const audio = new Audio(src);
                audio.volume = state.volume;

                // Track this sound
                set(s => ({ activeSounds: [...s.activeSounds, audio] }));

                audio.onended = () => {
                    // Remove from tracking when done
                    set(s => ({ activeSounds: s.activeSounds.filter(a => a !== audio) }));
                };

                // Handle play promise to avoid console errors
                audio.play().catch(err => {
                    console.warn('SFX Play failed', err);
                });
            },

            stopAll: () => {
                const { activeSounds } = get();
                activeSounds.forEach(audio => {
                    audio.pause();
                    audio.currentTime = 0;
                });
                set({ activeSounds: [] });
            },

            toggleMute: () => set(state => ({ isMuted: !state.isMuted })),

            setVolume: (val) => set({ volume: Math.max(0, Math.min(1, val)) }),
        }),
        {
            name: 'gorilla-sfx-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ volume: state.volume, isMuted: state.isMuted }), // Don't persist activeSounds
        }
    )
);
